import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { UserPlus, Mail, Lock, User, Building2, AlertCircle, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import TapTrackLogo from "../components/TapTrackLogo";

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: "",
    organisationName: "",
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!formData.organisationName.trim()) {
      setError("Organisation name is required");
      return;
    }
    
    setLoading(true);

    // Always register as organisation owner
    const result = await register({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: "owner",
      organisationName: formData.organisationName
    });
    setLoading(false);

    if (result.success) {
      toast.success("Organisation account created successfully!");
      navigate("/owner");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex flex-col">
      {/* Header */}
      <header className="p-6 flex items-center justify-between">
        <Link to="/">
          <TapTrackLogo size="large" />
        </Link>
        <Button 
          variant="ghost" 
          onClick={() => navigate("/")}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-slate-200/80 shadow-xl shadow-slate-200/50 bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-2xl font-black text-slate-900">
              Get Started with Engage
            </CardTitle>
            <CardDescription className="text-slate-400">
              Create your organisation account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div 
                  className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm"
                  data-testid="register-error"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="organisationName" className="text-slate-700 font-medium">
                  Organisation Name *
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="organisationName"
                    type="text"
                    placeholder="Your Charity Name"
                    value={formData.organisationName}
                    onChange={(e) => setFormData({ ...formData, organisationName: e.target.value })}
                    className="h-14 pl-10 text-base border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    data-testid="register-org-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 font-medium">
                  Your Full Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-14 pl-10 text-base border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  Email *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@organisation.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-14 pl-10 text-base border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  Password *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="h-14 pl-10 pr-12 text-base border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                    minLength={6}
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-base rounded-xl transition-colors duration-150 active:scale-[0.98]"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    Create Organisation
                  </span>
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-slate-500">
              Already have an account?{" "}
              <Link 
                to="/login" 
                className="text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
            <p className="mt-3 text-center text-xs text-slate-400">
              Fundraiser accounts are created by organisation owners
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center text-sm text-slate-400">
        <span className="font-medium text-slate-500">Engage</span> — Powered by OCI · © 2026 OCI Systems Ltd
      </footer>
    </div>
  );
}
